import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { B2InputGroup, B2Form } from "@hdb/biome-2";
import { FormControl, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { getMainSearchedAssets } from "src/store/slices/asset.slice";
interface SearchBarComponentProps {
  placeholder: string;
  methodToDispatch?: ({ PageNumber, pageSize, searchText }: any) => any;
  isDynamicSearch?: boolean;
  clearSearchResult?: boolean;
  extraParameter?: any;
  isUpdateFloorPlan?: boolean;
  isFloorPlan?: boolean;
  getSearch?: (val: string) => void;
  setIsAlreadyDispatched?: (isAlreadyDispatched: boolean) => void;
  setIsSearchBarEmpty?: (isSearchBarEmpty: boolean) => void;
  isAssetModule?: boolean;
  searchBarLength?: string;
}

const SearchBarComponent: FC<SearchBarComponentProps> = ({
  placeholder,
  methodToDispatch,
  isDynamicSearch,
  clearSearchResult,
  getSearch,
  extraParameter = null,
  isUpdateFloorPlan = false,
  isFloorPlan = false,
  setIsAlreadyDispatched,
  setIsSearchBarEmpty,
  isAssetModule = false,
  searchBarLength,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [loadingAssets, setLoadingAssets] = useState(false);
  const dispatch = useDispatch();
  const pageNumber = 1;
  const pageSize = 10;

  const assetMainSearchFilterValues = useSelector((state: any) => {
    return state.asset.assetMainSearchFilter;
  });

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (searchValue && methodToDispatch) {
      setLoadingAssets(true);

      if (setIsAlreadyDispatched) setIsAlreadyDispatched(false);

      try {
        if (isAssetModule) {
          await dispatch(
            getMainSearchedAssets({
              searchText: searchValue,
              tcCode: assetMainSearchFilterValues?.townCouncil,
              siteCode: assetMainSearchFilterValues?.site,
              blockCode: assetMainSearchFilterValues?.block,
              isUnassigned: null,
            })
          );
        } else {
          await dispatch(
            methodToDispatch({
              PageNumber: pageNumber,
              pageSize,
              searchText: searchValue,
              blockCode: extraParameter,
              isUnassigned: isAssetModule ? null : isUpdateFloorPlan,
            })
          );
        }
      } catch (error: any) {
        console.error("Error during dispatch:", error);
      } finally {
        setLoadingAssets(false);
        if (setIsAlreadyDispatched) setIsAlreadyDispatched(true);
      }
    }
  };

  useEffect(() => {
    if (setIsSearchBarEmpty) {
      if (searchValue.length === 0) setIsSearchBarEmpty(true);
      else setIsSearchBarEmpty(false);
    }
  }, [searchValue, setIsSearchBarEmpty]);

  const handleClear = () => {
    setSearchValue("");
    setLoadingAssets(false);

    if (setIsAlreadyDispatched) setIsAlreadyDispatched(false);
    getSearch && getSearch("");
  };

  const handleKeyUp = (e: any) => {
    if (e.keyCode === 8 && !searchValue) {
      handleClear();
    }
  };

  // Keep a ref to the latest handleSearch so the debounced
  // function never closes over stale state/props.
  const handleSearchRef = useRef(handleSearch);
  useEffect(() => {
    handleSearchRef.current = handleSearch;
  });

  // Create the debounced function exactly once, for the
  // lifetime of the component.
  const delayedSearchRef = useRef(
    _.debounce((e?: React.FormEvent) => handleSearchRef.current(e), 1000)
  );

  // Same pattern for the getSearch callback so parents don't get
  // notified (and refetch) on every keystroke.
  const getSearchRef = useRef(getSearch);
  useEffect(() => {
    getSearchRef.current = getSearch;
  });

  const debouncedGetSearchRef = useRef(
    _.debounce((val: string) => getSearchRef.current?.(val), 1000)
  );

  useEffect(() => {
    const delayed = delayedSearchRef.current;
    const debouncedGetSearch = debouncedGetSearchRef.current;

    if (isDynamicSearch && searchValue?.length >= 3) {
      delayed();
    }

    if (searchValue && getSearch) {
      debouncedGetSearch(searchValue);
    }

    return () => {
      delayed.cancel();
      debouncedGetSearch.cancel();
    };
  }, [searchValue]);

  useEffect(() => {
    if (clearSearchResult) {
      setSearchValue("");
      setLoadingAssets(false);
      if (setIsAlreadyDispatched) setIsAlreadyDispatched(false);
    }
  }, [clearSearchResult]);

  return (
    <B2Form onSubmit={handleSearch}>
      <B2InputGroup
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <FormControl
          style={{
            borderRight: searchValue && "none",
            height: "45px",
            // width: "250px",
            width: isUpdateFloorPlan
              ? searchBarLength || "650px"
              : isFloorPlan
              ? searchBarLength || "480px"
              : "250px",
            flex: "1",
          }}
          className="search-field"
          placeholder={placeholder}
          value={searchValue}
          onChange={(e: any) => setSearchValue(e.target.value)}
          onKeyUp={handleKeyUp}
        />

        <B2InputGroup.Text style={{ display: "flex", alignItems: "center" }}>
          <FontAwesomeIcon
            icon={faSearch}
            className="cursor-pointer"
            onClick={() => {
              if (!loadingAssets) {
                handleSearch();
              }
            }}
          />
        </B2InputGroup.Text>

        {loadingAssets && (
          <div
            style={{
              position: "absolute",
              right: "3rem",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none", // Prevent spinner from blocking interactions
              zIndex: 999, // Ensure it appears above the input field
            }}
          >
            <Spinner
              animation="border"
              variant="secondary"
              style={{ width: "1rem", height: "1rem" }}
            />
          </div>
        )}
      </B2InputGroup>
    </B2Form>
  );
};

export default SearchBarComponent;
